/**
 * Inline design references (design §16) — a TipTap extension that decorates two
 * token kinds in rendered design prose, decoration-only (the markdown on disk
 * round-trips untouched):
 *
 * - `#tag`     — theme/category. Click opens the tag view (seeded ⌘K palette)
 *                showing everything tagged.
 * - `@FirePit` — direct mention of a design entry by name (CamelCase or
 *                kebab-case). Click navigates straight to the concept; the
 *                caller falls back to a seeded search when ambiguous/missing.
 *
 * Clicking routes through the caller's handlers (DesignWorkspace's
 * openTagSearch / followMention).
 */
import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export interface InlineToken {
  /** Offset of the `#`/`@` within the text. */
  start: number;
  /** Offset past the last token character. */
  end: number;
  kind: 'tag' | 'mention';
  /** The token body without its sigil. */
  value: string;
}

/**
 * Find `#tag` / `@Mention` tokens in plain text. A token is the sigil +
 * alphanumeric start + [alphanumeric _ -] rest, not glued to a preceding
 * word/URL/email character (so `https://x.com/#anchor`, `a#b`, `C#` and
 * `pj@volley.com` don't light up).
 */
export function findInlineTokens(text: string): InlineToken[] {
  const re = /[#@]([A-Za-z0-9][A-Za-z0-9_-]*)/g;
  const out: InlineToken[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const prev = m.index > 0 ? text[m.index - 1] : '';
    if (prev && /[A-Za-z0-9_#@&./:]/.test(prev)) continue;
    out.push({
      start: m.index,
      end: m.index + m[0].length,
      kind: m[0][0] === '#' ? 'tag' : 'mention',
      value: m[1],
    });
  }
  return out;
}

/** The `#`/`@` token being typed at the end of `textBeforeCursor`, if any —
 *  drives the autocomplete popup. Same glue guards as findInlineTokens. */
export function activeTokenAt(
  textBeforeCursor: string,
): { kind: 'tag' | 'mention'; query: string; start: number } | null {
  const m = /[#@]([A-Za-z0-9][A-Za-z0-9_-]*)?$/.exec(textBeforeCursor);
  if (!m) return null;
  const prev = m.index > 0 ? textBeforeCursor[m.index - 1] : '';
  if (prev && /[A-Za-z0-9_#@&./:]/.test(prev)) return null;
  return {
    kind: textBeforeCursor[m.index] === '#' ? 'tag' : 'mention',
    query: m[1] ?? '',
    start: m.index,
  };
}

/** Autocomplete state reported to the host component while a `#`/`@` token is
 *  being typed: the doc range to replace and where to anchor the popup. */
export interface SuggestTokenState {
  kind: 'tag' | 'mention';
  query: string;
  /** Doc positions of the whole token (sigil included). */
  from: number;
  to: number;
  /** Viewport coords of the token start (from coordsAtPos) — popup anchor. */
  rect: { left: number; bottom: number };
}

function buildDecorations(
  doc: ProseMirrorNode,
  clickable: { tag: boolean; mention: boolean },
): DecorationSet {
  const decos: Decoration[] = [];
  doc.descendants((node, pos) => {
    // A `#`/`@` inside code is code, not a reference.
    if (node.type.spec.code) return false;
    if (!node.isText || !node.text) return true;
    if (node.marks.some((mark) => mark.type.name === 'code' || mark.type.name === 'link'))
      return true;
    for (const token of findInlineTokens(node.text)) {
      const isTag = token.kind === 'tag';
      const base = isTag ? 'tag-mention' : 'concept-mention';
      const isClickable = isTag ? clickable.tag : clickable.mention;
      decos.push(
        Decoration.inline(pos + token.start, pos + token.end, {
          class: `${base}${isClickable ? ` ${base}-clickable` : ''}`,
          [isTag ? 'data-tag-mention' : 'data-concept-mention']: token.value,
          ...(isClickable
            ? { title: isTag ? `Everything tagged “${token.value}”` : `Go to “${token.value}”` }
            : {}),
        }),
      );
    }
    return true;
  });
  return DecorationSet.create(doc, decos);
}

export interface TagMentionsOptions {
  /** `#tag` click — open the tag view (everything tagged). */
  onTagClick?: (tag: string) => void;
  /** `@Mention` click — navigate to the named concept. */
  onMentionClick?: (name: string) => void;
  /** Autocomplete: reports the `#`/`@` token being typed at the cursor (null when
   *  none). The host renders the popup and handles keys/insertion. Editable only. */
  onSuggestToken?: (state: SuggestTokenState | null) => void;
}

export const TagMentions = Extension.create<TagMentionsOptions>({
  name: 'tagMentions',

  addOptions() {
    return { onTagClick: undefined, onMentionClick: undefined };
  },

  addProseMirrorPlugins() {
    const { onTagClick, onMentionClick, onSuggestToken } = this.options;
    const clickable = { tag: Boolean(onTagClick), mention: Boolean(onMentionClick) };
    const plugins: Plugin[] = [
      new Plugin<DecorationSet>({
        key: new PluginKey('tagMentions'),
        state: {
          init: (_config, state) => buildDecorations(state.doc, clickable),
          apply: (tr, old) =>
            tr.docChanged ? buildDecorations(tr.doc, clickable) : old.map(tr.mapping, tr.doc),
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
          handleClick: (_view, _pos, event) => {
            const target = event.target as HTMLElement | null;
            const mentionEl = target?.closest?.('[data-concept-mention]');
            const name = mentionEl?.getAttribute('data-concept-mention');
            if (name && onMentionClick) {
              onMentionClick(name);
              return true;
            }
            const tagEl = target?.closest?.('[data-tag-mention]');
            const tag = tagEl?.getAttribute('data-tag-mention');
            if (tag && onTagClick) {
              onTagClick(tag);
              return true;
            }
            return false;
          },
        },
      }),
    ];

    if (onSuggestToken) {
      // Report the token being typed at the cursor (autocomplete). A view
      // plugin so we can translate doc positions to viewport coords.
      let last = '';
      const report = (state: SuggestTokenState | null): void => {
        const key = state ? JSON.stringify([state.kind, state.query, state.from, state.to]) : '';
        if (key === last) return;
        last = key;
        onSuggestToken(state);
      };
      plugins.push(
        new Plugin({
          key: new PluginKey('tagMentionSuggest'),
          view: () => ({
            update: (view) => {
              if (!view.editable || !view.hasFocus()) {
                report(null);
                return;
              }
              const { $from, empty } = view.state.selection;
              if (!empty || $from.parent.type.spec.code) {
                report(null);
                return;
              }
              const before = $from.parent.textBetween(0, $from.parentOffset, '\0', '\0');
              const token = activeTokenAt(before);
              if (!token) {
                report(null);
                return;
              }
              const from = $from.pos - (before.length - token.start);
              const coords = view.coordsAtPos(from);
              report({
                kind: token.kind,
                query: token.query,
                from,
                to: $from.pos,
                rect: { left: coords.left, bottom: coords.bottom },
              });
            },
            destroy: () => report(null),
          }),
          props: {
            handleDOMEvents: {
              blur: (view) => {
                // Let a popup mousedown land before dismissing.
                setTimeout(() => {
                  if (!view.hasFocus()) report(null);
                }, 150);
                return false;
              },
            },
          },
        }),
      );
    }

    return plugins;
  },
});
